import * as React from "react";
import { RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/Button";

/**
 * The last line before a white screen.
 *
 * `ErrorState` in `Feedback.tsx` handles a *fetch* that failed, which is the
 * common case and the one every page already covers. It cannot help with a
 * render that threw - a null a component did not expect, a bad date, a map over
 * something that turned out not to be an array. React's answer to an unhandled
 * error during render is to unmount the whole tree, so without a boundary
 * anywhere, one bad row in one table blanks the entire console and the only
 * clue is in the browser console.
 *
 * ── Why it wraps the outlet rather than the app ─────────────────────────────
 *
 * Placed inside `AppShell`, the rail and the topbar survive, so the reader can
 * navigate away from the broken page instead of reloading and landing back on
 * it. Placed around `<App />` it would take the navigation down with the page,
 * which is the same dead end one step removed.
 *
 * ── Still a class component, and it has to be ───────────────────────────────
 *
 * There is no hook equivalent. `componentDidCatch` and
 * `getDerivedStateFromError` are the entire public API for this, and React has
 * shipped no function-component version. This is the one class in the codebase.
 */
interface Props {
  children: React.ReactNode;
  /** Changing this resets the boundary - the router passes the pathname. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Nothing collects this yet - crash reporting is Phase 7. Until then the
    // console is the only place it goes, and printing the component stack is
    // what makes the difference between "something broke" and a file name.
    console.error("Console render error:", error, info.componentStack);
  }

  componentDidUpdate(previous: Props) {
    // Navigating away from a page that threw should not keep showing its error.
    // Without this the boundary latches, and every subsequent route renders the
    // failure of the one before it.
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="page">
        <div className="boundary">
          <h1 className="type-heading boundary__title">This page stopped working</h1>
          <p className="boundary__body">
            Something went wrong while drawing it. Nothing you were looking at has been changed -
            this is a display fault, not a lost save.
          </p>
          <p className="boundary__detail mono">{this.state.error.message}</p>
          <div className="boundary__actions">
            <Button icon={<RotateCcw />} onClick={() => this.setState({ error: null })}>
              Try again
            </Button>
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Reload the console
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
